import React, { useRef, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from 'components/layouts/pageTitle';
import { Config } from 'configs';
import { Context as AuthContext } from 'contexts/auth';
import { DataColumn, LinkColumn, List } from 'components/lists';
import { getTranslatedData } from 'locales';
import { useAggregate } from 'hooks';
import { Loader } from 'components/indicators/loader';
import { formFileUrlFromFileArrayWithExternalResized } from 'utils/imageHelper';
import { FormButton } from 'components/buttons/formButton';
import { TemplateVariationForm } from './_form_template_variation';

export const VariationTemplatesAddEditForm = ({ itemData, isEdit }) => {
  const urlRootName = 'variation-templates';
  const navigate = useNavigate();
  const formRef = useRef(null);

  const { state: auth } = useContext(AuthContext);

  const [isSaving, setIsSaving] = useState(false);

  const onSubmitClicked = () => {
    formRef.current.click();
  };

  const requestData = {
    collectionName: Config.DATA.COLLECTIONS.VARIATION_TEMPLATES,
    aggregateId: Config.DATA.AGGREGATES.GET_VARIATION_TEMPLATE_ATTACHED_PRODUCTS,
    secretKey: auth.token,
    filterBuilder: () => {
      return { templateId: itemData._id };
    },
    loadOnInit: false,
    dontRunOnInit: true,
    aggregateIdV2: 'AdminGetAttachedTemplateProducts',
    companyIdForV2: auth.company?.company,
  };

  const [findAttachedProducts, attachedProducts, isLoadingAttachedProducts] = useAggregate(requestData);

  useEffect(() => {
    if (itemData && itemData.attachable) {
      findAttachedProducts();
    }
  }, []);

  return (
    <>
      <PageTitle
        title="Product variation template"
        breadcrumbs={[{ path: '/settings', title: 'Settings' }]}
      />

      <div className="container mx-auto px-2 md:px-6 lg:px-8">
        <div className="w-full">
          <div className="shadow sm:rounded-md sm:overflow-hidden">
            <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {isEdit ? ` Editing ${itemData?.template_name}` : 'Add new variation template'}
                </h3>
              </div>
              <TemplateVariationForm
                itemData={itemData}
                isEdit={isEdit}
                ref={formRef}
                isProductVariation={false}
                setIsSaving={setIsSaving}
              />
            </div>
            <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
              <button
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => navigate(`/settings/${urlRootName}`)}
              >
                Cancel
              </button>

              <FormButton onClick={onSubmitClicked} text={isEdit ? 'Save' : 'Add new'} isLoading={isSaving} />
            </div>
          </div>

          {itemData && itemData.attachable && (
          <div className="bg-white shadow sm:rounded-md overflow-hidden mt-10">
            <div className="pt-6 px-4 space-y-6 sm:px-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">Attached products</h3>
                <p className="mt-1 text-sm text-gray-500">All products this template is attached to</p>
              </div>
            </div>

            {!!isLoadingAttachedProducts && <div className="mt-6 mb-6"><Loader small /></div>}

            {!isLoadingAttachedProducts && attachedProducts && attachedProducts.length > 0 && (
            <div className="px-4 sm:px-6 mb-6">
              <List source={attachedProducts} onRowClick={() => {}}>
                <DataColumn
                  name="Image"
                  render={(product) => {
                    const imageUrl = formFileUrlFromFileArrayWithExternalResized(product.list_image, product.list_image_resized);

                    if (!imageUrl) {
                      return null;
                    }

                    return (
                      <div
                        className="w-10 h-10 rounded-lg bg-no-repeat bg-center bg-cover"
                        style={{ backgroundImage: `url(${imageUrl})` }}
                      />
                    );
                  }}
                  dbColumnName="list_image"
                  headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                  dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-700 sm:table-cell"
                />
                <DataColumn
                  name="Product"
                  dbColumnName="name"
                  headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                  render={(item) => getTranslatedData(item.name)}
                />
                <LinkColumn
                  title="Open"
                  onClick={(product) => navigate(`/products/${product._id}`)}
                  className="relative py-3.5 pl-3 sm:pr-6"
                  dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                />
              </List>
            </div>
            )}
            {!isLoadingAttachedProducts && (!attachedProducts || attachedProducts.length === 0) && <div className="mb-6" />}
          </div>
          )}

        </div>
      </div>
    </>
  );
};
